import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  ViewModule as GridViewIcon,
  ViewList as TableViewIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  CertificateListItem,
  MaterialGrade,
  Surface,
  Lieferant,
  Country,
  MaterialNorm,
} from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../services/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import CertificateForm from '../components/CertificateForm';
import CompactMetalStatus from '../components/CompactMetalStatus';
import CertificateTable, { type CertificateSortColumn } from '../components/CertificateTable';
import PageHeader from '../components/PageHeader';
import FilterBar from '../components/FilterBar';
import CertificateExcelDialog from '../components/CertificateExcelDialog';
import FilterDatePicker from '../components/FilterDatePicker';
import { useTableSort } from '../utils/tableSort';
import CountrySelect from '../components/CountrySelect';
import { useDebounce } from '../hooks/useDebounce';
import { downloadBlob } from '../utils/fileUtils';
import { useToast } from '../components/ToastProvider';
import {
  CERTIFICATE_FILTERS_KEY,
  formatStoredDay,
  readStoredFilters,
  storedDay,
  storedId,
  storedText,
  writeStoredFilters,
} from '../utils/filterStorage';
import { apiErrorMessage } from '../utils/apiError';

const CertificatesPage: React.FC = () => {
  const [rows, setRows] = useState<CertificateListItem[]>([]);
  const hasRows = useRef(false);
  const [total, setTotal] = useState(0);
  const [unfilteredTotal, setUnfilteredTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();
  const [storedFilters] = useState(() => readStoredFilters(CERTIFICATE_FILTERS_KEY));
  const [searchText, setSearchText] = useState(() => storedText(storedFilters.search));
  const [lieferantId, setLieferantId] = useState<number | null>(() => storedId(storedFilters.lieferantId));
  const [materialGradeId, setMaterialGradeId] = useState<number | null>(() =>
    storedId(storedFilters.materialGradeId)
  );
  const [surfaceId, setSurfaceId] = useState<number | null>(() => storedId(storedFilters.surfaceId));
  const [meltCountryId, setMeltCountryId] = useState<number | null>(() =>
    storedId(storedFilters.meltCountryId)
  );
  const [originCountryId, setOriginCountryId] = useState<number | null>(() =>
    storedId(storedFilters.originCountryId)
  );
  const [materialNormId, setMaterialNormId] = useState<number | null>(() =>
    storedId(storedFilters.materialNormId)
  );
  const [dateFromFilter, setDateFromFilter] = useState<Dayjs | null>(() => storedDay(storedFilters.dateFrom));
  const [dateToFilter, setDateToFilter] = useState<Dayjs | null>(() => storedDay(storedFilters.dateTo));
  const [page, setPage] = useState(1);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      storedId(storedFilters.lieferantId) != null ||
      storedId(storedFilters.materialGradeId) != null ||
      storedId(storedFilters.surfaceId) != null ||
      storedId(storedFilters.meltCountryId) != null ||
      storedId(storedFilters.originCountryId) != null ||
      storedId(storedFilters.materialNormId) != null ||
      storedDay(storedFilters.dateFrom) != null ||
      storedDay(storedFilters.dateTo) != null
  );
  const [dateResetKey, setDateResetKey] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateListItem | null>(null);
  const [materialGrades, setMaterialGrades] = useState<MaterialGrade[]>([]);
  const [surfaces, setSurfaces] = useState<Surface[]>([]);
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [materialNorms, setMaterialNorms] = useState<MaterialNorm[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');

  const { sort, order, onSort } = useTableSort<CertificateSortColumn>('certificate_date', 'desc', [
    'certificate_date',
  ]);
  const { user, canWrite, isViewer } = useAuth();
  const navigate = useNavigate();

  const dateFromParam =
    dateFromFilter?.isValid() && dateFromFilter.year() >= 1900
      ? dateFromFilter.format('YYYY-MM-DD')
      : undefined;
  const dateToParam =
    dateToFilter?.isValid() && dateToFilter.year() >= 1900
      ? dateToFilter.format('YYYY-MM-DD')
      : undefined;
  const itemsPerPage = viewMode === 'table' ? 500 : 12;

  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    Promise.all([
      apiService.getMaterialGrades(),
      apiService.getSurfaces(),
      apiService.getLieferanten(),
      apiService.getCountries(),
      apiService.getMaterialNorms(),
    ])
      .then(([gradesData, surfacesData, lieferantenData, countriesData, normsData]) => {
        setMaterialGrades(gradesData);
        setSurfaces(surfacesData);
        setLieferanten(lieferantenData);
        setCountries(countriesData);
        setMaterialNorms(normsData);
      })
      .catch((err) => console.error('Error loading filters:', err));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearchText,
    lieferantId,
    materialGradeId,
    surfaceId,
    meltCountryId,
    originCountryId,
    materialNormId,
    dateFromParam,
    dateToParam,
    sort,
    order,
    viewMode,
  ]);

  const loadPage = React.useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const data = await apiService.getCertificatePage(
        {
          q: debouncedSearchText || undefined,
          lieferant_id: lieferantId ?? undefined,
          material_grade_id: materialGradeId ?? undefined,
          surface_id: surfaceId ?? undefined,
          melt_country_id: meltCountryId ?? undefined,
          country_id: originCountryId ?? undefined,
          material_norm_id: materialNormId ?? undefined,
          date_from: dateFromParam,
          date_to: dateToParam,
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          sort,
          order,
        },
        signal
      );
      setRows(data.items);
      hasRows.current = true;
      setTotal(data.total);
      setUnfilteredTotal(data.unfiltered_total);
      setError(null);
    } catch (err) {
      if (axios.isCancel(err)) return;
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 422 && hasRows.current) {
        setError(apiErrorMessage(err, 'Die Suche ist zu lang.'));
        return;
      }
      setError('Fehler beim Laden der Lieferanten Zertifikate');
      console.error('Error loading data:', err);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setLoadedOnce(true);
      }
    }
  }, [
    debouncedSearchText,
    lieferantId,
    materialGradeId,
    surfaceId,
    meltCountryId,
    originCountryId,
    materialNormId,
    dateFromParam,
    dateToParam,
    sort,
    order,
    page,
    itemsPerPage,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    void loadPage(controller.signal);
    return () => controller.abort();
  }, [loadPage]);

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const paginatedCertificates = rows;

  useEffect(() => {
    writeStoredFilters(CERTIFICATE_FILTERS_KEY, {
      search: searchText,
      lieferantId,
      materialGradeId,
      surfaceId,
      meltCountryId,
      originCountryId,
      materialNormId,
      dateFrom: formatStoredDay(dateFromFilter),
      dateTo: formatStoredDay(dateToFilter),
    });
  }, [
    searchText,
    lieferantId,
    materialGradeId,
    surfaceId,
    meltCountryId,
    originCountryId,
    materialNormId,
    dateFromFilter,
    dateToFilter,
  ]);

  const handleClearFilters = () => {
    setSearchText('');
    setLieferantId(null);
    setMaterialGradeId(null);
    setSurfaceId(null);
    setMeltCountryId(null);
    setOriginCountryId(null);
    setMaterialNormId(null);
    setDateFromFilter(null);
    setDateToFilter(null);
    setDateResetKey((key) => key + 1);
    setPage(1);
  };

  const handleDeleteClick = (certificate: CertificateListItem) => {
    setCertificateToDelete(certificate);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!certificateToDelete) return;

    try {
      await apiService.deleteCertificate(certificateToDelete.id);
      setDeleteDialogOpen(false);
      setCertificateToDelete(null);
      void loadPage();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Löschen des Lieferanten Zertifikats'));
      console.error('Error deleting certificate:', err);
    }
  };

  async function handleDownloadPdf(certificateId: number): Promise<void> {
    try {
      const blob = await apiService.downloadCertificatePdf(certificateId);
      downloadBlob(blob, `zertifikat-${certificateId}.pdf`);
    } catch (err) {
      toastError(apiErrorMessage(err, 'Fehler beim Herunterladen der PDF'));
      console.error('Error downloading PDF:', err);
    }
  }

  function canEditCertificate(certificate: CertificateListItem): boolean {
    return (
      user?.role === 'KALOTTE' || (user?.role === 'RUBY' && certificate.business_unit === 'RUBY')
    );
  }

  if (!loadedOnce) {
    return <LoadingSpinner message="Lieferanten Zertifikate werden geladen..." />;
  }

  const lieferantFilter = lieferanten.find((item) => item.id === lieferantId) ?? null;
  const materialGradeFilter = materialGrades.find((item) => item.id === materialGradeId) ?? null;
  const surfaceFilter = surfaces.find((item) => item.id === surfaceId) ?? null;
  const meltCountryFilter = countries.find((item) => item.id === meltCountryId) ?? null;
  const originCountryFilter = countries.find((item) => item.id === originCountryId) ?? null;
  const materialNormFilter = materialNorms.find((item) => item.id === materialNormId) ?? null;
  const activeFilterCount = [
    lieferantId,
    materialGradeId,
    surfaceId,
    meltCountryId,
    originCountryId,
    materialNormId,
    dateFromParam,
    dateToParam,
  ].filter(Boolean).length;

  return (
    <Box>
      <PageHeader
        title="Lieferanten Zertifikate"
        count={total}
        total={unfilteredTotal}
        actions={
          <>
            <Box sx={{ display: 'flex', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setViewMode('table')}
                color={viewMode === 'table' ? 'primary' : 'default'}
                title="Tabellenansicht"
              >
                <TableViewIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setViewMode('grid')}
                color={viewMode === 'grid' ? 'primary' : 'default'}
                title="Kartenansicht"
              >
                <GridViewIcon />
              </IconButton>
            </Box>
            {!isViewer && (
              <Button variant="outlined" onClick={() => setExcelOpen(true)}>
                Excel
              </Button>
            )}
            {canWrite && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateDialog(true)}>
                Neues Lieferanten Zertifikat
              </Button>
            )}
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FilterBar
        search={searchText}
        onSearch={setSearchText}
        searchPlaceholder="Zertifikatsnummer, Lieferant, Schmelzland..."
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((open) => !open)}
        activeCount={activeFilterCount}
        onClear={handleClearFilters}
      >
        <Autocomplete
          options={lieferanten}
          getOptionLabel={(option) => `${option.name} (${option.short_name})`}
          value={lieferantFilter}
          onChange={(_, newValue) => setLieferantId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Lieferant" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <Autocomplete
          options={materialGrades}
          getOptionLabel={(option) => option.name}
          value={materialGradeFilter}
          onChange={(_, newValue) => setMaterialGradeId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Materialgüte" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <Autocomplete
          options={surfaces}
          getOptionLabel={(option) => option.name}
          value={surfaceFilter}
          onChange={(_, newValue) => setSurfaceId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Oberfläche" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <CountrySelect
          countries={countries}
          value={meltCountryFilter}
          onChange={(country) => setMeltCountryId(country?.id ?? null)}
          showMeltPourCode
          size="small"
        />
        <CountrySelect
          countries={countries}
          value={originCountryFilter}
          onChange={(country) => setOriginCountryId(country?.id ?? null)}
          label="Ursprungsland"
          size="small"
        />
        <Autocomplete
          options={materialNorms}
          getOptionLabel={(option) => option.name}
          value={materialNormFilter}
          onChange={(_, newValue) => setMaterialNormId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Werkstoffnorm" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <FilterDatePicker
          label="Von Datum"
          resetKey={dateResetKey}
          defaultValue={dateFromFilter}
          onCommit={setDateFromFilter}
        />
        <FilterDatePicker
          label="Bis Datum"
          resetKey={dateResetKey + 1}
          defaultValue={dateToFilter}
          onCommit={setDateToFilter}
        />
      </FilterBar>

      {viewMode === 'table' ? (
        <>
          <CertificateTable
            certificates={paginatedCertificates}
            sort={sort}
            order={order}
            onSort={onSort}
            onDelete={handleDeleteClick}
            onDownloadPdf={handleDownloadPdf}
            canEditCertificate={canEditCertificate}
          />
          {total > itemsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedCertificates.map((certificate) => (
              <Grid item xs={12} sm={6} md={4} key={certificate.id}>
                <Card>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" component="h2" noWrap>
                          {certificate.certificate_number}
                        </Typography>
                        {certificate.blocked && (
                          <Typography variant="body2" color="warning.main" sx={{ fontSize: 12 }}>
                            Unvollständig
                          </Typography>
                        )}
                      </Box>
                      {certificate.has_pdf && (
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadPdf(certificate.id)}
                          title="PDF herunterladen"
                        >
                          <PdfIcon color="error" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography color="textSecondary" gutterBottom>
                      {certificate.lieferant_name ?? '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {dayjs(certificate.certificate_date).format('DD.MM.YYYY')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={certificate.material_grade_name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {certificate.surface_name && (
                        <Chip
                          label={certificate.surface_name}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {certificate.metal_values?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <CompactMetalStatus
                          metalValues={certificate.metal_values}
                          materialGradeCompositions={certificate.grade_compositions || []}
                        />
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      Erstellt von: {certificate.creator_full_name || '—'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/certificates/${certificate.id}`)}
                      title="Anzeigen"
                    >
                      <ViewIcon />
                    </IconButton>
                    {canEditCertificate(certificate) && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/certificates/${certificate.id}`)}
                          title="Bearbeiten"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(certificate)}
                          title="Löschen"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {rows.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                Keine Lieferanten Zertifikate gefunden
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Versuchen Sie, die Suchfilter anzupassen oder ein neues Lieferanten Zertifikat zu
                erstellen.
              </Typography>
            </Box>
          )}

          {total > itemsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <CertificateExcelDialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onImported={() => {
          void loadPage();
        }}
        canImport={canWrite}
      />

      {/* Create Certificate Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Neues Lieferanten Zertifikat erstellen</DialogTitle>
        <DialogContent>
          <CertificateForm
            onSuccess={() => {
              setShowCreateDialog(false);
              void loadPage();
            }}
            onCancel={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Lieferanten Zertifikat löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie das Lieferanten Zertifikat "
            {certificateToDelete?.certificate_number}" löschen möchten? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificatesPage;
